import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ChevronLeft,
    ChevronRight,
    Send,
    Trash2,
    CheckCircle2,
    HelpCircle,
    Target,
} from "lucide-react";
import { BASE_URL, practicePyqQuestionsApi } from "../../../../api/api";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { token } from "../../../../api/auth";

export default function PersonalMentorship() {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [showAnswer, setShowAnswer] = useState(false);
    const [answeredQuestions, setAnsweredQuestions] = useState({}); // {questionId: option}

    // Load saved answers if exist
    useEffect(() => {
        const savedAnswers = localStorage.getItem("savedAnswers");
        if (savedAnswers) {
            setAnsweredQuestions(JSON.parse(savedAnswers));
        }
    }, []);

    // Fetch questions from API
    useEffect(() => {
        axios
            .get(`${BASE_URL}${practicePyqQuestionsApi}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                if (response.data?.data) {
                    setQuestions(response.data.data);
                } else {
                    setQuestions([]);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching questions:", error);
                toast.error(
                    "Failed to load questions. Please try again later.",
                );
                setLoading(false);
            });
    }, []);

    // Sync selectedOption when question or draft changes
    useEffect(() => {
        if (questions[currentIndex]) {
            setSelectedOption(
                answeredQuestions[questions[currentIndex].id] || null,
            );
        }
    }, [currentIndex, answeredQuestions, questions]);

    // Jump to last answered question on load
    useEffect(() => {
        if (questions.length > 0) {
            const savedAnswers = JSON.parse(
                localStorage.getItem("savedAnswers") || "{}",
            );
            if (Object.keys(savedAnswers).length > 0) {
                const answeredIds = Object.keys(savedAnswers).map((id) =>
                    Number(id),
                );
                const lastAnsweredId = Math.max(...answeredIds);
                const lastIndex = questions.findIndex(
                    (q) => q.id === lastAnsweredId,
                );
                if (lastIndex !== -1) {
                    setCurrentIndex(lastIndex);
                }
            }
        }
    }, [questions]);

    // Save selected option instantly
    const handleOptionChange = (questionId, option) => {
        setSelectedOption(option);
        setAnsweredQuestions((prev) => {
            const updated = { ...prev, [questionId]: option };
            localStorage.setItem("savedAnswers", JSON.stringify(updated));
            return updated;
        });
    };

    const handleNext = () => {
        if (currentIndex < questions.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setShowAnswer(false);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setShowAnswer(false);
        }
    };

    const handleClearDraft = () => {
        localStorage.removeItem("savedAnswers");
        setAnsweredQuestions({});
        setSelectedOption(null);
        toast.info("Draft cleared successfully.");
    };

    const handleSubmit = async () => {
        const studentSession = sessionStorage.getItem("studentSession");
        if (!studentSession) {
            localStorage.setItem(
                "savedAnswers",
                JSON.stringify(answeredQuestions),
            );
            localStorage.setItem("returnUrl", window.location.pathname);
            toast.warning("Please log in to submit your answers.", {
                autoClose: 1500,
                onClose: () =>
                    (window.location.href = `/login?redirect=${encodeURIComponent(
                        window.location.pathname,
                    )}`),
            });
            return;
        }

        const studentId = JSON.parse(studentSession).id;
        const currentQuestion = questions[currentIndex];

        if (currentQuestion && selectedOption) {
            setAnsweredQuestions((prev) => {
                const updated = {
                    ...prev,
                    [currentQuestion.id]: selectedOption,
                };
                localStorage.setItem("savedAnswers", JSON.stringify(updated));
                return updated;
            });
        }

        const finalAnswers = {
            ...answeredQuestions,
            ...(currentQuestion && selectedOption
                ? { [currentQuestion.id]: selectedOption }
                : {}),
        };

        const payload = Object.keys(finalAnswers).map((questionId) => ({
            student_id: studentId,
            question_id: Number(questionId),
            marked_option: finalAnswers[questionId],
        }));

        if (payload.length === 0) {
            toast.warning(
                "Please answer at least one question before submitting.",
            );
            return;
        }

        try {
            const submitPromises = payload.map((item) =>
                axios.post(`${BASE_URL}${practicePyqQuestionsApi}`, item, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            );

            await Promise.all(submitPromises);

            toast.success("All answered questions submitted successfully!");
            setShowAnswer(true);

            localStorage.removeItem("savedAnswers");
            setAnsweredQuestions({});
            setCurrentIndex(0);
            setSelectedOption(null);
        } catch (err) {
            console.error("Error submitting answers:", err);
            toast.error(
                "Failed to submit some answers. Please try again later.",
            );
        }
    };

    // Animation variants matching established theme
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    if (!loading && questions.length === 0) return null;

    const currentQuestion = questions[currentIndex];

    return (
        <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 w-full border-t border-gray-200 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Standardized Editorial Header */}
                <div className="mb-12">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-3">
                            <span className="w-6 h-1 bg-red-700"></span>
                            Objective Assessment
                        </div>
                        <h2 className="text-4xl md:text-5xl font-black text-neutral-900 mb-4 ">
                            Practice{" "}
                            <span className="text-yellow-500">PYQs.</span>
                        </h2>
                        <p className="text-gray-500 text-lg font-medium leading-relaxed max-w-2xl">
                            Test your knowledge with previous year questions.
                            Track your progress, save drafts, and evaluate your
                            readiness.
                        </p>
                    </motion.div>
                </div>

                {loading ? (
                    <div className="w-full h-96 bg-white rounded-xl border border-gray-200 animate-pulse"></div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        {/* Left Column: Active Question Panel */}
                        <motion.div
                            className="lg:col-span-8 flex flex-col"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={currentQuestion.id}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{
                                        duration: 0.3,
                                        ease: "easeOut",
                                    }}
                                    className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 md:p-8 flex flex-col flex-grow"
                                >
                                    {/* Meta Tags */}
                                    <div className="flex flex-wrap items-center justify-between mb-6 pb-6 border-b border-gray-100">
                                        <span className="inline-flex items-center px-3 py-1 bg-neutral-900 text-white rounded font-bold text-xs tracking-widest uppercase shadow-sm">
                                            <Target
                                                size={12}
                                                className="mr-1.5 text-yellow-400"
                                            />
                                            Question {currentIndex + 1}
                                        </span>
                                        <span className="text-sm font-bold text-gray-400">
                                            {currentIndex + 1} of{" "}
                                            {questions.length}
                                        </span>
                                    </div>

                                    {/* Question Text */}
                                    <div className="text-xl font-bold text-neutral-900 leading-relaxed mb-8">
                                        {currentQuestion.question}
                                    </div>

                                    {/* Options (Custom styled radios) */}
                                    <div className="flex flex-col gap-3 mt-auto">
                                        {[
                                            currentQuestion.option_1,
                                            currentQuestion.option_2,
                                            currentQuestion.option_3,
                                            currentQuestion.option_4,
                                        ]
                                            .filter(Boolean)
                                            .map((opt, idx) => {
                                                const isSelected =
                                                    selectedOption === opt;
                                                const labelLetters = [
                                                    "A",
                                                    "B",
                                                    "C",
                                                    "D",
                                                ];
                                                return (
                                                    <label
                                                        key={idx}
                                                        className={`flex items-center p-4 rounded-xl cursor-pointer transition-all duration-300 border-2 ${
                                                            isSelected
                                                                ? "border-yellow-400 bg-yellow-50 shadow-sm"
                                                                : "border-gray-100 hover:border-yellow-400 hover:bg-gray-50"
                                                        }`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name={`option_${currentQuestion.id}`}
                                                            value={opt}
                                                            checked={isSelected}
                                                            onChange={() =>
                                                                handleOptionChange(
                                                                    currentQuestion.id,
                                                                    opt,
                                                                )
                                                            }
                                                            className="hidden"
                                                        />

                                                        {/* Custom Radio Button Circle */}
                                                        <div
                                                            className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center mr-4 transition-colors ${
                                                                isSelected
                                                                    ? "border-yellow-500"
                                                                    : "border-gray-300"
                                                            }`}
                                                        >
                                                            {isSelected && (
                                                                <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                                                            )}
                                                        </div>

                                                        <span className="w-6 text-sm font-bold text-gray-400 mr-2">
                                                            {labelLetters[idx]}.
                                                        </span>
                                                        <span
                                                            className={`text-base font-medium ${isSelected ? "text-neutral-900" : "text-gray-600"}`}
                                                        >
                                                            {opt}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                    </div>
                                </motion.div>
                            </AnimatePresence>

                            {/* Navigation Controls */}
                            <div className="flex items-center justify-between mt-6 gap-4">
                                <button
                                    onClick={handlePrev}
                                    disabled={currentIndex === 0}
                                    className={`flex items-center px-6 py-3 rounded-lg font-bold text-sm transition-all duration-300 ${currentIndex === 0 ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white border border-gray-200 text-neutral-900 hover:border-neutral-900 hover:shadow-sm"}`}
                                >
                                    <ChevronLeft size={18} className="mr-1" />{" "}
                                    Previous
                                </button>

                                {currentIndex < questions.length - 1 ? (
                                    <button
                                        onClick={handleNext}
                                        className="flex items-center px-8 py-3 bg-neutral-900 hover:bg-yellow-400 hover:text-neutral-900 text-white font-bold rounded-lg shadow-sm transition-colors duration-300 group"
                                    >
                                        Next Question{" "}
                                        <ChevronRight
                                            size={18}
                                            className="ml-1 group-hover:translate-x-1 transition-transform"
                                        />
                                    </button>
                                ) : (
                                    <button
                                        onClick={handleSubmit}
                                        className="flex items-center px-8 py-3 bg-red-700 hover:bg-neutral-900 text-white font-bold rounded-lg shadow-md transition-colors duration-300 group"
                                    >
                                        Submit Practice{" "}
                                        <Send
                                            size={16}
                                            className="ml-2 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform"
                                        />
                                    </button>
                                )}
                            </div>
                        </motion.div>

                        {/* Right Column: Sidebar */}
                        <motion.div
                            className="lg:col-span-4 flex flex-col gap-6"
                            variants={itemVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {/* Progress Tracker */}
                            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                                <h3 className="text-sm font-bold text-neutral-900 mb-4 uppercase tracking-widest border-b border-gray-100 pb-3">
                                    Session Progress
                                </h3>

                                <div className="flex flex-wrap gap-2">
                                    {questions.map((q, idx) => {
                                        const isAnswered =
                                            !!answeredQuestions[q.id];
                                        const isCurrent = currentIndex === idx;
                                        return (
                                            <button
                                                key={q.id}
                                                onClick={() =>
                                                    setCurrentIndex(idx)
                                                }
                                                className={`w-10 h-10 rounded-lg text-sm font-bold flex items-center justify-center transition-all duration-300 ${
                                                    isCurrent
                                                        ? "bg-neutral-900 text-white shadow-md"
                                                        : isAnswered
                                                          ? "bg-green-100 text-green-700 border border-green-200"
                                                          : "bg-gray-50 text-gray-500 border border-gray-200 hover:border-neutral-900 hover:text-neutral-900"
                                                }`}
                                            >
                                                {idx + 1}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Draft List */}
                            {Object.keys(answeredQuestions).length > 0 && (
                                <div className="bg-white rounded-xl border border-gray-200 p-0 shadow-sm overflow-hidden flex flex-col max-h-[400px]">
                                    <div className="p-5 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
                                        <h3 className="text-sm font-bold text-neutral-900 uppercase tracking-widest">
                                            Saved Drafts
                                        </h3>
                                        <span className="bg-yellow-400 text-neutral-900 text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-widest">
                                            {
                                                Object.keys(answeredQuestions)
                                                    .length
                                            }
                                        </span>
                                    </div>

                                    <div className="overflow-y-auto p-3 flex-1 custom-scrollbar">
                                        <ul className="space-y-2">
                                            {Object.entries(
                                                answeredQuestions,
                                            ).map(([qid, answer]) => {
                                                const qIndex =
                                                    questions.findIndex(
                                                        (q) =>
                                                            q.id ===
                                                            Number(qid),
                                                    );
                                                if (qIndex === -1) return null;

                                                return (
                                                    <li
                                                        key={qid}
                                                        onClick={() =>
                                                            setCurrentIndex(
                                                                qIndex,
                                                            )
                                                        }
                                                        className="p-3 rounded-lg border border-gray-100 hover:border-yellow-400 hover:bg-yellow-50/30 cursor-pointer transition-colors group flex items-start gap-3"
                                                    >
                                                        <CheckCircle2
                                                            size={16}
                                                            className="mt-0.5 text-green-500"
                                                        />
                                                        <div className="flex-1 overflow-hidden">
                                                            <p className="text-xs font-bold text-neutral-900 mb-0.5">
                                                                Question{" "}
                                                                {qIndex + 1}
                                                            </p>
                                                            <p className="text-[11px] text-gray-500 truncate">
                                                                Marked: {answer}
                                                            </p>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>

                                    <div className="p-4 border-t border-gray-100 bg-gray-50 mt-auto">
                                        <button
                                            onClick={handleClearDraft}
                                            className="w-full flex items-center justify-center px-4 py-2 bg-white border border-gray-200 text-red-600 hover:bg-red-50 hover:border-red-200 rounded font-bold text-xs uppercase tracking-widest transition-colors duration-300"
                                        >
                                            <Trash2
                                                size={14}
                                                className="mr-2"
                                            />{" "}
                                            Clear All Drafts
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </div>
        </section>
    );
}
